/**
 * Consultation Store - Refactored with unified collaborative architecture
 *
 * This store manages consultation form state, multi-module collaborative locking, and form data.
 * Supports four collaborative modules: consultNoteDiagnosis, letterMcs, nursingNote, dispensing
 * Migrated to use the new unified state management patterns.
 */

import type { VisitDischargeAdvice } from "@/models/discharge-advice";
import type { LetterMCResponse } from "@/services/lettersMc";
import type { CollaborativeModule, LockSnapshot } from "@/types/collaborative";
import type { AsyncStoreActions, BaseStore } from "../core";
import { createAsyncActions, createStore, withLogging } from "../core";

// Multi-module collaboration state interface
interface ModuleCollaborativeState {
  isLocked: boolean;
  lockSnapshot: LockSnapshot | null;
  lastSaved: Date | null;
  isDirty: boolean;
}

// Form data interface
interface ConsultationFormData {
  // Doctor Note & Diagnosis
  consultantNote: string;
  diagnoses: Array<{
    icd10DiagnosisCode?: string;
    snomedDiagnosisIdId?: string;
    remarks?: string;
  }>;

  // Nursing Note
  nursingNote: string;

  // Letters/MC - No specific fields needed, managed through lock state

  // Dispensing & Charting
  dispensingData: Record<string, unknown>;
  chartingData: Record<string, unknown>;
}

// Consultation store state interface
interface ConsultationStoreState extends BaseStore, AsyncStoreActions {
  // Current consultation ID being accessed
  currentConsultationId: string | null;

  // Multi-module collaboration state
  collaborativeStates: Record<CollaborativeModule, ModuleCollaborativeState>;

  // Form data
  formData: ConsultationFormData;

  // 🎯 Letters/MCs certificates data - persisted across component lifecycle
  certificates: Record<string, LetterMCResponse[]>; // Key: visitId
  certificatesInitialized: Record<string, boolean>; // Track initialization per visit

  // 🎯 Patient Education discharge advice data - same lifecycle model as Letters/MCs
  visitDischargeAdvices: Record<string, VisitDischargeAdvice[]>; // Key: visitId
  visitDischargeAdvicesInitialized: Record<string, boolean>; // Track initialization per visit

  // Global connection state
  isSocketConnected: boolean;
  socketError: string | null;

  // Actions for consultation management
  setCurrentConsultationId: (id: string | null) => void;

  // 🎯 Actions for certificates management
  setCertificates: (visitId: string, certificates: LetterMCResponse[]) => void;
  getCertificates: (visitId: string) => LetterMCResponse[];
  isCertificatesInitialized: (visitId: string) => boolean;
  markCertificatesInitialized: (visitId: string) => void;
  clearCertificates: (visitId: string) => void;

  // 🎯 Actions for Patient Education discharge advice management
  setVisitDischargeAdvices: (visitId: string, records: VisitDischargeAdvice[]) => void;
  getVisitDischargeAdvices: (visitId: string) => VisitDischargeAdvice[];
  isVisitDischargeAdvicesInitialized: (visitId: string) => boolean;
  markVisitDischargeAdvicesInitialized: (visitId: string) => void;
  clearVisitDischargeAdvices: (visitId: string) => void;

  // Actions for collaborative states
  setModuleLockState: (
    module: CollaborativeModule,
    state: Partial<ModuleCollaborativeState>,
  ) => void;
  updateModuleLockSnapshot: (module: CollaborativeModule, snapshot: LockSnapshot | null) => void;
  setModuleLastSaved: (module: CollaborativeModule, timestamp: Date) => void;
  setModuleDirty: (module: CollaborativeModule, isDirty: boolean) => void;

  // Actions for form data
  updateFormData: (updates: Partial<ConsultationFormData>) => void;
  updateConsultantNote: (note: string) => void;
  updateNursingNote: (note: string) => void;
  updateDiagnoses: (diagnoses: ConsultationFormData["diagnoses"]) => void;

  // Socket connection actions
  setSocketConnected: (connected: boolean) => void;
  setSocketError: (error: string | null) => void;

  // Utility actions
  getModuleState: (module: CollaborativeModule) => ModuleCollaborativeState;
  isModuleEditable: (module: CollaborativeModule, currentUserId?: string) => boolean;
  hasUnsavedChanges: () => boolean;

  reset: () => void;
}

// Create initial module collaboration state
const createInitialModuleState = (): ModuleCollaborativeState => ({
  isLocked: false,
  lockSnapshot: null,
  lastSaved: null,
  isDirty: false,
});

// Initial form data
const initialFormData: ConsultationFormData = {
  consultantNote: "",
  diagnoses: [],
  nursingNote: "",
  dispensingData: {},
  chartingData: {},
};

// Initial state
const initialState = {
  currentConsultationId: null,
  collaborativeStates: {
    consultNoteDiagnosis: createInitialModuleState(),
    letterMcs: createInitialModuleState(),
    nursingNote: createInitialModuleState(),
    dispensing: createInitialModuleState(),
  } as Record<CollaborativeModule, ModuleCollaborativeState>,
  formData: initialFormData,
  certificates: {},
  certificatesInitialized: {},
  visitDischargeAdvices: {},
  visitDischargeAdvicesInitialized: {},
  isSocketConnected: false,
  socketError: null,
};

export const useConsultationStore = createStore<ConsultationStoreState>(
  withLogging("ConsultationStore", (set, get) => ({
    ...initialState,
    ...createAsyncActions<ConsultationStoreState>(),
    _initialized: false,
    _lastUpdated: Date.now(),

    // Basic actions for consultation management
    setCurrentConsultationId: (id: string | null) =>
      set((state) => {
        state.currentConsultationId = id;
        state._lastUpdated = Date.now();
      }),

    // 🎯 Certificates actions
    setCertificates: (visitId: string, certificates: LetterMCResponse[]) =>
      set((state) => {
        state.certificates[visitId] = certificates;
        state._lastUpdated = Date.now();
      }),

    getCertificates: (visitId: string) => {
      const state = get();
      return state.certificates[visitId] || [];
    },

    isCertificatesInitialized: (visitId: string) => {
      const state = get();
      return Boolean(state.certificatesInitialized[visitId]);
    },

    markCertificatesInitialized: (visitId: string) =>
      set((state) => {
        state.certificatesInitialized[visitId] = true;
        state._lastUpdated = Date.now();
      }),

    clearCertificates: (visitId: string) =>
      set((state) => {
        delete state.certificates[visitId];
        delete state.certificatesInitialized[visitId];
        state._lastUpdated = Date.now();
      }),

    // 🎯 Patient Education discharge advice actions
    setVisitDischargeAdvices: (visitId: string, records: VisitDischargeAdvice[]) =>
      set((state) => {
        state.visitDischargeAdvices[visitId] = records;
        state._lastUpdated = Date.now();
      }),

    getVisitDischargeAdvices: (visitId: string) => {
      const state = get();
      return state.visitDischargeAdvices[visitId] || [];
    },

    isVisitDischargeAdvicesInitialized: (visitId: string) => {
      const state = get();
      return Boolean(state.visitDischargeAdvicesInitialized[visitId]);
    },

    markVisitDischargeAdvicesInitialized: (visitId: string) =>
      set((state) => {
        state.visitDischargeAdvicesInitialized[visitId] = true;
        state._lastUpdated = Date.now();
      }),

    clearVisitDischargeAdvices: (visitId: string) =>
      set((state) => {
        delete state.visitDischargeAdvices[visitId];
        delete state.visitDischargeAdvicesInitialized[visitId];
        state._lastUpdated = Date.now();
      }),

    // Actions for collaborative states
    setModuleLockState: (module: CollaborativeModule, updates: Partial<ModuleCollaborativeState>) =>
      set((state) => {
        state.collaborativeStates[module] = {
          ...state.collaborativeStates[module],
          ...updates,
        };
        state._lastUpdated = Date.now();
      }),

    updateModuleLockSnapshot: (module: CollaborativeModule, snapshot: LockSnapshot | null) =>
      set((state) => {
        state.collaborativeStates[module].lockSnapshot = snapshot;
        state.collaborativeStates[module].isLocked = Boolean(snapshot?.lockedBy);
        state._lastUpdated = Date.now();
      }),

    setModuleLastSaved: (module: CollaborativeModule, timestamp: Date) =>
      set((state) => {
        state.collaborativeStates[module].lastSaved = timestamp;
        state.collaborativeStates[module].isDirty = false;
        state._lastUpdated = Date.now();
      }),

    setModuleDirty: (module: CollaborativeModule, isDirty: boolean) =>
      set((state) => {
        state.collaborativeStates[module].isDirty = isDirty;
        state._lastUpdated = Date.now();
      }),

    // Actions for form data
    updateFormData: (updates: Partial<ConsultationFormData>) =>
      set((state) => {
        state.formData = { ...state.formData, ...updates };
        state._lastUpdated = Date.now();
      }),

    updateConsultantNote: (note: string) =>
      set((state) => {
        state.formData.consultantNote = note;
        state.collaborativeStates.consultNoteDiagnosis.isDirty = true;
        state._lastUpdated = Date.now();
      }),

    updateNursingNote: (note: string) =>
      set((state) => {
        state.formData.nursingNote = note;
        state.collaborativeStates.nursingNote.isDirty = true;
        state._lastUpdated = Date.now();
      }),

    updateDiagnoses: (diagnoses: ConsultationFormData["diagnoses"]) =>
      set((state) => {
        state.formData.diagnoses = diagnoses;
        state.collaborativeStates.consultNoteDiagnosis.isDirty = true;
        state._lastUpdated = Date.now();
      }),

    // Socket connection actions
    setSocketConnected: (connected: boolean) =>
      set((state) => {
        state.isSocketConnected = connected;
        if (!connected) {
          // Reset all lock states when connection is lost
          Object.keys(state.collaborativeStates).forEach((module) => {
            state.collaborativeStates[module as CollaborativeModule].isLocked = false;
            state.collaborativeStates[module as CollaborativeModule].lockSnapshot = null;
          });
        }
        state._lastUpdated = Date.now();
      }),

    setSocketError: (error: string | null) =>
      set((state) => {
        state.socketError = error;
        state._lastUpdated = Date.now();
      }),

    // Utility actions
    getModuleState: (module: CollaborativeModule) => {
      const state = get();
      return state.collaborativeStates[module];
    },

    isModuleEditable: (module: CollaborativeModule, currentUserId?: string) => {
      const state = get();
      const moduleState = state.collaborativeStates[module];
      return Boolean(
        moduleState.lockSnapshot &&
          moduleState.lockSnapshot.lockedBy === currentUserId &&
          moduleState.isLocked,
      );
    },

    hasUnsavedChanges: () => {
      const state = get();
      return Object.values(state.collaborativeStates).some((moduleState) => moduleState.isDirty);
    },

    reset: () =>
      set((state) => {
        Object.assign(state, initialState);
        state.loading = false;
        state.error = null;
        state._initialized = false;
        state._lastUpdated = Date.now();
      }),
  })),
  {
    name: "echo-consultation-storage",
    // Not persisted (ECHO-2318): consultation notes, diagnoses, letters/MCs and discharge advice
    // must not be written to localStorage. Nothing reads the store back after a reload: the
    // form data is write-only, and certificates/discharge advice re-initialise from the visit
    // fetched from the server (a persisted "initialized" flag used to pin stale copies instead).
    persist: false,
  },
);

// Selector hooks for better performance
export const useConsultationId = () => useConsultationStore((state) => state.currentConsultationId);

export const useConsultationFormData = () => useConsultationStore((state) => state.formData);

export const useModuleCollaborativeState = (module: CollaborativeModule) =>
  useConsultationStore((state) => state.collaborativeStates[module]);

export const useSocketConnectionState = () =>
  useConsultationStore((state) => ({
    isConnected: state.isSocketConnected,
    error: state.socketError,
  }));

// 🎯 Certificate selector hooks - with stable empty array reference
const EMPTY_CERTIFICATES: LetterMCResponse[] = [];
const EMPTY_VISIT_DISCHARGE_ADVICES: VisitDischargeAdvice[] = [];

export const useStoreCertificates = (visitId: string) =>
  useConsultationStore((state) => state.certificates[visitId] || EMPTY_CERTIFICATES);

export const useStoreVisitDischargeAdvices = (visitId: string) =>
  useConsultationStore(
    (state) => state.visitDischargeAdvices[visitId] || EMPTY_VISIT_DISCHARGE_ADVICES,
  );

// Individual action hooks for better stability
export const useSetCertificates = () => useConsultationStore((state) => state.setCertificates);
export const useGetCertificates = () => useConsultationStore((state) => state.getCertificates);
export const useIsCertificatesInitialized = () =>
  useConsultationStore((state) => state.isCertificatesInitialized);
export const useMarkCertificatesInitialized = () =>
  useConsultationStore((state) => state.markCertificatesInitialized);
export const useClearCertificates = () => useConsultationStore((state) => state.clearCertificates);
export const useSetVisitDischargeAdvices = () =>
  useConsultationStore((state) => state.setVisitDischargeAdvices);
export const useGetVisitDischargeAdvices = () =>
  useConsultationStore((state) => state.getVisitDischargeAdvices);
export const useIsVisitDischargeAdvicesInitialized = () =>
  useConsultationStore((state) => state.isVisitDischargeAdvicesInitialized);
export const useMarkVisitDischargeAdvicesInitialized = () =>
  useConsultationStore((state) => state.markVisitDischargeAdvicesInitialized);
export const useClearVisitDischargeAdvices = () =>
  useConsultationStore((state) => state.clearVisitDischargeAdvices);

export const useConsultationActions = () => {
  const store = useConsultationStore.getState();
  return {
    setCurrentConsultationId: store.setCurrentConsultationId,
    updateFormData: store.updateFormData,
    updateConsultantNote: store.updateConsultantNote,
    updateNursingNote: store.updateNursingNote,
    updateDiagnoses: store.updateDiagnoses,
    setModuleLockState: store.setModuleLockState,
    updateModuleLockSnapshot: store.updateModuleLockSnapshot,
    setModuleLastSaved: store.setModuleLastSaved,
    setModuleDirty: store.setModuleDirty,
    setSocketConnected: store.setSocketConnected,
    setSocketError: store.setSocketError,
    getModuleState: store.getModuleState,
    isModuleEditable: store.isModuleEditable,
    hasUnsavedChanges: store.hasUnsavedChanges,
    reset: store.reset,
  };
};

// Computed selectors
export const useHasUnsavedChanges = () =>
  useConsultationStore((state) => state.hasUnsavedChanges());

export const useIsAnyModuleLocked = () =>
  useConsultationStore((state) =>
    Object.values(state.collaborativeStates).some((moduleState) => moduleState.isLocked),
  );
